import type React from 'react';
import { Bell, Package, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Notification } from '@/types/models';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkRead }) => {
  const timeAgo = formatRelativeTime(notification.created_at);

  return (
    <div
      className={cn(
        'flex gap-3 p-4 rounded-2xl transition-colors',
        notification.is_read
          ? 'bg-white'
          : 'bg-primary-50/60 border border-primary-100',
      )}
    >
      <div
        className={cn(
          'shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
          notification.is_read
            ? 'bg-gray-100 text-gray-400'
            : 'bg-primary-100 text-primary-600',
        )}
      >
        {notification.order_id ? (
          <Package className="w-5 h-5" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-relaxed',
            notification.is_read ? 'text-gray-600' : 'text-gray-900 font-medium',
          )}
        >
          {notification.message_body || notification.message_template || 'Уведомление'}
        </p>
        <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
      </div>

      {!notification.is_read && onMarkRead && (
        <button
          onClick={() => onMarkRead(notification.id)}
          className="shrink-0 p-2 rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors self-start"
          title="Отметить прочитанным"
        >
          <CheckCheck className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'Только что';
  if (diffMin < 60) return `${diffMin} мин. назад`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ч. назад`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} дн. назад`;

  return date.toLocaleDateString('ru-RU');
}

export default NotificationItem;
